import { z } from 'zod';
import { scopeSchema } from './scopes.js';
/**
 * The working layer's boundary shapes (ADR-0029).
 *
 * Two properties are load-bearing here, and both are shapes rather than
 * rules somebody has to remember:
 *
 *   **Nothing names a destination.** Constitution §6: a caller supplies a
 *   tool id, never a URL. So no schema in this file has a member that could
 *   hold one — not the message transport, not the chat context, not a job
 *   payload. `work.test.ts` asserts that by walking the schemas rather than
 *   by reading them.
 *
 *   **Context is identifiers, never contents.** The side chat's context is
 *   the second injection surface in the product, after inbound mail. A
 *   context assembled from row *contents* would put an injected Deal note in
 *   front of a model without passing ingest scanning, so the shape is closed
 *   and the agent fetches the records it names through the scanned reader.
 *   ADR-0028 made this argument for the experience payload; this is the same
 *   argument applied to the surface that came next.
 */
// `guid`, not `uuid`: Postgres stores any 8-4-4-4-12 hex value, and
// `z.uuid()` additionally demands an RFC 9562 version and variant. A row id
// that came from a provider, a fixture or an older generator is still a valid
// primary key, and refusing it here would reject rows the database holds.
const uuid = z.guid();
/**
 * A mailbox address, lowercased.
 *
 * Lowercased here rather than trusted, because Google returns whatever
 * casing the Admin console happened to hold and `work.mailbox.address` has
 * a CHECK requiring lower case. A payload that failed that CHECK would
 * fail inside a job handler, which is a long way from the push endpoint
 * that wrote it.
 */
const mailboxAddress = z
    .string()
    .min(3)
    .max(320)
    .transform((value) => value.trim().toLowerCase())
    .pipe(z.string().regex(/^[^@\s]+@[^@\s]+$/, 'a mailbox address'));
const identifier = z.string().regex(/^[a-z][a-z_0-9]*$/);
const entityName = z.string().regex(/^[A-Z][A-Za-z0-9]*$/);
/**
 * An opaque provider id: a Gmail message id, a Chat space name, a Drive file
 * id. Bounded, and refused if it looks like somewhere to send something.
 *
 * A provider id is not a URL, and something that parses as one arrived from
 * the wrong place. Checking here is cheap; discovering it at the egress
 * boundary means the value already travelled.
 */
export const externalIdSchema = z
    .string()
    .min(1)
    .max(512)
    .refine((v) => !/^[a-z][a-z0-9+.-]*:\/\//i.test(v), {
    message: 'an external id is an opaque provider id, never a URL (§6)',
});
// ---------------------------------------------------------------------------
// Closed sets
// ---------------------------------------------------------------------------
// Each one matches a CHECK constraint in migrations 0013–0017. They are
// exported because the frontend needs exactly these and no more: a surface
// that offers a seventh timeline action is offering something the database
// will refuse.
export const channelSchema = z.enum(['email', 'chat', 'whatsapp', 'note']);
export const directionSchema = z.enum(['inbound', 'outbound', 'internal']);
export const messageStateSchema = z.enum([
    'draft',
    'pending_approval',
    'queued',
    'sent',
    'delivered',
    'failed',
    'received',
]);
export const scanVerdictSchema = z.enum(['pending', 'clean', 'flagged', 'blocked']);
export const threadStateSchema = z.enum(['open', 'waiting_reply', 'snoozed', 'closed']);
export const timelineItemKindSchema = z.enum([
    'calendar_event',
    'task',
    'reply_needed',
    'brief',
    'chase',
    // The only kind that can be on more than one person's day at once: an
    // unclaimed item on a shared mailbox is every member's until somebody
    // takes it (migration 0030).
    'shared_inbox',
    // Two conversations that look like one, on the day of the person who owns
    // the contact, for them to merge or keep apart (ADR-0031, migration 0047).
    'merge_proposal',
    'break',
    'note',
]);
export const dayPlanOpSchema = z.enum(['place', 'move', 'hide', 'pin', 'postpone', 'restore']);
export const taskStateSchema = z.enum(['open', 'in_progress', 'waiting', 'done', 'cancelled']);
export const draftOutcomeSchema = z.enum([
    'pending',
    'sent_unchanged',
    'sent_edited',
    'discarded',
]);
export const jobKindSchema = z.enum([
    'prepare_brief',
    'draft_reply',
    'ingest_scan',
    'detect_chase',
    'sync_calendar',
    'run_task',
    // A twin's task waiting on a tool: the superadmin told on Google Chat and
    // Telegram, as Kai, at the channels a superadmin added (ADR-0139).
    'notify_blocker',
    // Reconciling one mailbox with Google: the history the push did not
    // deliver, the delegate list, and the watch before it expires.
    'sync_mailbox',
    // Mirroring a person's own inbox for them alone, read by their twin
    // under their mail consent (ADR-0120).
    'sync_own_mailbox',
    // The change loop (ADR-0074): a person's words compiled into a spec by a
    // model (work-api), the spec built and tested in a sandbox, and an
    // approved build promoted (both dept-runtime, which holds the owner and
    // migrator sessions a build needs and no customer mail).
    'compile_spec',
    'build_feature',
    'promote_feature',
    // A twin drafting the next piece for a project, in its principal's voice,
    // where a project assignment says it may (ADR-0059).
    'draft_publication',
    // Prospecting (ADR-0064): found through Places, read from the
    // restaurant's own site, enriched through Hunter, drafted from the
    // department's shared box. Each runs as the agent holding
    // `<department>.prospect`, and nothing sends.
    'discover_places',
    'read_site',
    'enrich_contact',
    'draft_first_touch',
    // One first touch leaving under an approved campaign, at the recipient's
    // working hour (ADR-0079, ADR-0086). Judged again at the moment it runs.
    'send_outreach',
    // Customer.io, under the grant that assigns it (ADR-0087): identify one
    // lifecycle contact, or tell Customer.io they unsubscribed; delete an
    // erased one's profile.
    'sync_lifecycle',
    'delete_lifecycle',
    // One event Customer.io is told about, on the basis it needs (ADR-0102).
    'track_lifecycle',
    // A captured meeting's notes Doc, read after it ends through the person's
    // drive consent, scanned, and turned into commitments they confirm
    // (ADR-0092).
    'read_meeting_notes',
    // Customer.io's own count of profiles and messages, read once a day as the
    // agent that holds the grant, so what it counts governs when it is the
    // higher (ADR-0095).
    'reconcile_customerio',
    // One journey's ask for an approved mailing, sent from Gmail as the
    // declared agent once everything is judged again (ADR-0099).
    'send_mailing',
    // Inbound messages recorded without a scan, scanned so they may be read
    // (ADR-0116). Work-api's: it holds the key the bodies are sealed under.
    'rescan_pending',
    // §10's proposing pass for one department with evidence. Work-api's: a
    // proposal's reasoning is sealed into the lead's task (ADR-0118).
    'propose_improvements',
    // A running plan's next cells, swept through Places under the plan's own
    // assignment; and the same for one day with nothing written outside our
    // index (ADR-0134, R7). Work-api's: tool calls through the gateway.
    'sweep_plan',
    'dry_run_plan',
    // Never queued: the heartbeat shreds the index itself, across every
    // department, each tick (ADR-0129). The kind stays because the job table's
    // CHECK names it and schema changes are additive; a job of it that
    // somehow arrives fails, saying so, rather than shredding one department.
    'shred_index',
    // A research answer another department sent, written onto this one's own
    // market record (§18, migration 0107). Work-api's: the answer is sealed.
    'record_research',
    // The ECB's reference rates for the currencies our markets price in,
    // through the gateway (ADR-0135). Work-api's: a tool call.
    'fetch_fx',
    // A department's market sources read for signals: each feed or page a
    // person attached in sources.yaml, through its own manifest (ADR-0141).
    // Work-api's: it reads the ledger and calls tools.
    'read_sources',
    // One department's cadences moved on: the next step prepared as a
    // person's task, and every stop rule checked (ADR-0136, migration 0108).
    // Work-api's: a prospect's address is sealed.
    'advance_cadences',
    // One prospect's own site read again a month on, and what is new on it
    // since the last read made a signal (R2, migration 0110). Work-api's: a
    // tool call through the gateway.
    'watch_site',
    // A meeting on a project (Amendment 0009, ADR-0125): one participating
    // agent's turn, run in its own department's session, and the minutes,
    // run in the minuting department's. Work-api's: a model call, and what
    // is said is sealed under the project's key.
    'meeting_turn',
    'meeting_minutes',
    // The department's agent saying more about a task a person asked it to:
    // steps, what done looks like, what is unclear (ADR-0149). Work-api's: the
    // task and its notes are sealed.
    'elaborate_task',
    'run_held',
    // What the platform's own pages, tools, steps and decisions mean, as
    // vectors (ADR-0152). Work-api's: a model call, on the EU route.
    'embed_index',
    // A shortlist an assistant proposed and a person confirmed, resolved
    // through the plan's own Places search (ADR-0153). Work-api's.
    'resolve_shortlist',
    // A finding an assistant proposed and a person confirmed, read back
    // through the department's site reader (ADR-0153). Work-api's.
    'verify_evidence',
    // The monthly manufacturer search a lead turned on after ten manual runs
    // (ADR-0163). Work-api's: it asks Nia, which needs the department's key.
    'auto_supplier_research',
    // What the department's agent makes of any record a person asked about
    // (ADR-0165). Work-api's: the record's readers and the person's key.
    'elaborate_record',
    // Once a day, what is stuck, told to every superadmin's side chat and, as
    // Kai, to the channels a superadmin added (ADR-0169). Work-api's: it goes
    // through the gateway.
    'notify_stalled',
    // A superadmin's twin keeping the platform (Amendment 0013, ADR-0172):
    // one read under the standing assignment, or a write on its record
    // through core-api; and the heartbeat reading a long write back.
    // Work-api's: the gateway, and the person's key that seals the answer.
    'run_operation',
    'poll_operation',
    // The superadmin's twin's night: reads under the standing assignment,
    // findings by rule, proposals and briefs (ADR-0174). Work-api's.
    'nightly_audit',
    // What a research answer is good for, proposed by the asking department's
    // agent and held against the answer (ADR-0175). Work-api's: the answer is
    // sealed under the request's key, and a model reads it.
    'find_research_uses',
    // A person's request for coded duplicate evidence, run by the evaluator
    // the platform assigned. The payload names only the immutable request;
    // it carries no record content, prompt, model or agent destination.
    'evaluate_identity',
    // A person's request for coded inferred-link evidence. The payload carries
    // only the immutable request id; current evidence and assignment are read
    // again by the job.
    'evaluate_subject_link',
]);
export const requestStateSchema = z.enum(['open', 'accepted', 'refused', 'done', 'cancelled']);
export const unresolvedReasonSchema = z.enum([
    'unknown_sender',
    'no_subject_match',
    'low_confidence',
    'out_of_scope',
]);
// ---------------------------------------------------------------------------
// What a request needs (§18, ADR-0039)
// ---------------------------------------------------------------------------
// A request between departments names what doing it would take, so the
// receiver can answer "do we already hold this?" before anyone starts. Tool
// ids and scopes only — the same two things an assignment grants — and
// closed, because the next field somebody adds here is "instructions".
//
// The check is against what the receiving department ALREADY holds. A
// request confers nothing: one that names a tool nobody there has is refused,
// not fulfilled by granting it. That is the sentence amendment 0003 adds to
// §15, as a schema.
const toolId = z.string().regex(/^[a-z][a-z_]*\.[a-z][a-z_0-9]*$/);
export const requestNeedsSchema = z
    .object({
    tools: z.array(toolId).max(20).default([]),
    scopes: z.array(scopeSchema).max(20).default([]),
})
    .strict();
// ---------------------------------------------------------------------------
// What something is about
// ---------------------------------------------------------------------------
// A Contact and a Company are core rows with real foreign keys. A Deal is a
// `flex` row whose table may not exist yet, so it is a checked triple —
// `work.subject_link` says why the database cannot hold that one, and this is
// the write-time check that stands in for it.
export const subjectRefSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('contact'), id: uuid }).strict(),
    z.object({ kind: z.literal('company'), id: uuid }).strict(),
    z
        .object({
        kind: z.literal('flex'),
        department: identifier,
        entity: entityName,
        id: uuid,
    })
        .strict(),
]);
export const subjectRelationSchema = z.enum(['about', 'from', 'to', 'cc', 'owner', 'blocks']);
/** Stable text for a subject, for keys and log lines. Never a destination. */
export function subjectKey(subject) {
    return subject.kind === 'flex'
        ? `flex:${subject.department}.${subject.entity}:${subject.id}`
        : `${subject.kind}:${subject.id}`;
}
// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------
// Per channel, and closed. `space` and `phoneNumberId` are the two values
// that decide *where* a message goes, which is exactly why they are resolved
// from a record rather than accepted from a model — see `boundParams` in the
// gateway surface resolver.
export const transportSchema = z.discriminatedUnion('channel', [
    z
        .object({
        channel: z.literal('email'),
        /** RFC 5322 Message-ID of the message this replies to, if any. */
        inReplyTo: externalIdSchema.optional(),
        references: z.array(externalIdSchema).max(50).default([]),
        /** A machine answered for the person (RFC 3834); an out-of-office ends a cadence as one (ADR-0136). */
        autoReply: z.literal(true).optional(),
        // Sent by a machine: a notifier, a list, a no-reply sender (ADR-0159).
        automated: z.literal(true).optional(),
    })
        .strict(),
    z
        .object({
        channel: z.literal('chat'),
        space: externalIdSchema,
        threadName: externalIdSchema.optional(),
    })
        .strict(),
    z
        .object({ channel: z.literal('whatsapp'), phoneNumberId: externalIdSchema })
        .strict(),
    z.object({ channel: z.literal('note') }).strict(),
]);
// ---------------------------------------------------------------------------
// The side chat's context
// ---------------------------------------------------------------------------
export const chatPageSchema = z.enum([
    'today',
    'contact',
    'company',
    'thread',
    'task',
    'project',
    'department',
    'calendar',
    'spec',
    // Prospecting and market validation (proposal 0009, backend proposal 7).
    'market',
    'profile',
    'territory',
    'plan',
    'prospect',
    'signal',
    'buyer',
    'supplier',
    'offer',
    // A request between departments (§18), so the chat can say where it is (ADR-0169).
    'request',
    'institution',
    'document',
    'brief',
    'incident',
    'shortlist',
    'profile_change',
    'introduction',
    'index_entry',
]);
export const chatContextSchema = z
    .object({
    workingDate: z.string().date().optional(),
    /** Which surface the person has open. An enum, so it cannot carry prose. */
    page: chatPageSchema,
    /** What that surface is showing. Ids, resolved by the reader. */
    contactId: uuid.optional(),
    companyId: uuid.optional(),
    threadId: uuid.optional(),
    taskId: uuid.optional(),
    projectId: uuid.optional(),
    calendarEventId: uuid.optional(),
    department: identifier.optional(),
    specId: z.string().min(1).max(120).optional(),
    /** A country code, as a market's record is keyed. */
    marketCode: z.string().regex(/^[A-Z]{2}$/).optional(),
    profileId: uuid.optional(),
    territoryId: uuid.optional(),
    planId: uuid.optional(),
    prospectId: uuid.optional(),
    signalId: uuid.optional(),
    buyerId: uuid.optional(),
    supplierId: uuid.optional(),
    offerId: uuid.optional(),
    /** A request between departments in view (§18), read in the asking session and never across it (ADR-0169). */
    requestId: uuid.optional(),
    programmeId: uuid.optional(),
    briefId: uuid.optional(),
    incidentId: uuid.optional(),
    shortlistId: uuid.optional(),
    profileChangeId: uuid.optional(),
    introductionId: uuid.optional(),
    indexEntryId: uuid.optional(),
    documentId: z.string().regex(/^[A-Za-z0-9_-]{5,200}$/).optional(),
    documentFolderKey: z.string().regex(/^[A-Za-z0-9_./-]{1,200}$/).optional(),
    /**
     * The address of the page in view, which the server looks up in the
     * pages' own help (page-help.ts) to say what the page is for. An address
     * and nothing else: letters, digits and / - _ . only, no query, no prose.
     */
    route: z.string().regex(/^\/[A-Za-z0-9/_.-]{0,200}$/).optional(),
})
    // Strict, and this is the enforcement: a `pageText`, a `selection`, a
    // `summary` — anything a well-meaning frontend might add to "give the agent
    // more context" — is refused here rather than carried to a model unscanned.
    .strict();
/**
 * Which one record a side-chat context names, so the screen offers
 * "Ask the agent to elaborate" exactly where the server will accept it. The
 * most specific first: a prospect's page also names its market, and it is
 * the prospect that was asked about.
 */
export function recordOf(context) {
    const order = [
        ['task', context.taskId],
        ['thread', context.threadId],
        ['prospect', context.prospectId],
        ['offer', context.offerId],
        ['supplier', context.supplierId],
        ['plan', context.planId],
        ['profile', context.profileId],
        ['territory', context.territoryId],
        ['contact', context.contactId],
        ['company', context.companyId],
        ['market', context.marketCode],
    ];
    const hit = order.find(([, id]) => typeof id === 'string' && id.length > 0);
    return hit ? { kind: hit[0], id: hit[1] } : null;
}
// ---------------------------------------------------------------------------
// Job payloads
// ---------------------------------------------------------------------------
export const jobPayloadSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('prepare_brief'), forKind: z.enum(['calendar_event', 'task', 'thread', 'contact']), forId: uuid, forHuman: uuid }).strict(),
    z.object({ kind: z.literal('draft_reply'), threadId: uuid, forHuman: uuid }).strict(),
    // Both fields are what the handler needs and neither was there.
    //
    // `messageId` is **Gmail's own opaque id**, not a uuid. It was typed as
    // one because the push endpoint synthesised it by hashing the address
    // and the history id — an id that identifies no message, handed to a
    // handler whose job is to fetch a message by id. The push endpoint's own
    // comment said the notification "names no message"; what it should
    // enqueue is a reconcile, and that is now `sync_mailbox`.
    //
    // `mailbox` is which address it arrived in. Without it the handler
    // cannot impersonate the right box to fetch the body, and the message
    // cannot be filed against whoever that box belongs to.
    z
        .object({
        kind: z.literal('ingest_scan'),
        messageId: z.string().min(1).max(256).regex(/^[A-Za-z0-9_-]+$/, 'a Gmail message id'),
        mailbox: mailboxAddress,
    })
        .strict(),
    z.object({ kind: z.literal('detect_chase'), forHuman: uuid }).strict(),
    // `part` narrows a read to what a push said changed (ADR-0096): their own
    // calendar, their list and its shares, or one calendar they follow.
    // Absent is everything, which is what the backstop reads.
    z
        .object({
        kind: z.literal('sync_calendar'),
        calendarRef: z.string().min(1).max(320),
        part: z.enum(['all', 'own', 'list', 'calendar']).optional(),
        subscriptionId: uuid.optional(),
    })
        .strict()
        .refine((p) => (p.part === 'calendar') === (p.subscriptionId !== undefined), {
        message: 'a calendar part names its subscription, and only it does',
    }),
    // An agent picking up a task routed to it (§18). A task id and nothing
    // else: what to do is on the task, sealed, and a payload that carried the
    // instruction would be an instruction nobody signed.
    z.object({ kind: z.literal('run_task'), taskId: uuid }).strict(),
    // A person asked the department's agent to say more about a task
    // (ADR-0149). The task and who asked; what the agent reads is the task and
    // its notes, fenced, when the job runs.
    z.object({ kind: z.literal('elaborate_task'), taskId: uuid, forHuman: uuid }).strict(),
    // The record is named by the side chat's own identifiers, strict, so a
    // page's prose can never ride along into the prompt (§11).
    z.object({ kind: z.literal('elaborate_record'), context: chatContextSchema, forHuman: uuid }).strict(),
    // A lead approved what an agent proposed (ADR-0151): the held run by id,
    // and nothing else. The calls are sealed on it, read when the job runs.
    z.object({ kind: z.literal('run_held'), heldId: uuid }).strict(),
    z.object({ kind: z.literal('embed_index') }).strict(),
    z.object({ kind: z.literal('resolve_shortlist'), shortlistId: uuid }).strict(),
    z.object({ kind: z.literal('verify_evidence'), proposalId: uuid }).strict(),
    z.object({ kind: z.literal('auto_supplier_research') }).strict(),
    // The day and two counts, and nothing else: no title, no error, no person.
    z.object({
        kind: z.literal('notify_stalled'),
        day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        requests: z.number().int().min(0).max(100_000),
        kinds: z.number().int().min(0).max(1_000),
    }).strict(),
    // An operation and whose it is, and nothing else: what it reads and with
    // which values is on its row, which only a superadmin's session sees.
    z.object({ kind: z.literal('run_operation'), operationId: uuid, forHuman: uuid, sessionId: uuid.optional() }).strict(),
    z.object({ kind: z.literal('poll_operation'), operationId: uuid, forHuman: uuid }).strict(),
    // Whose night, and which: what is read is the standing assignment's.
    z.object({ kind: z.literal('nightly_audit'), forHuman: uuid, day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) }).strict(),
    // Which answer, and who pressed: what it found is on the request's uses.
    z.object({ kind: z.literal('find_research_uses'), requestId: uuid, forHuman: uuid }).strict(),
    z.object({ kind: z.literal('evaluate_identity'), requestId: uuid }).strict(),
    z.object({ kind: z.literal('evaluate_subject_link'), requestId: uuid }).strict(),
    // A task and the tool it waits on, and nothing else: who to tell and in
    // what words is the platform's, read when the job runs.
    z.object({ kind: z.literal('notify_blocker'), taskId: uuid, toolId: z.string().regex(/^[a-z][a-z_]*\.[a-z][a-z_0-9]*$/) }).strict(),
    z.object({ kind: z.literal('sync_mailbox'), address: mailboxAddress }).strict(),
    // Whose inbox, by id and never by address: the address is read from the
    // roster when the job runs, so a payload cannot name another mailbox.
    z.object({ kind: z.literal('sync_own_mailbox'), humanId: uuid }).strict(),
    // The change loop (ADR-0074). Identifiers only: the words are on the
    // change request and the spec is on its version, and a payload that
    // carried either would be a second copy nobody confirmed.
    z.object({ kind: z.literal('compile_spec'), changeRequestId: uuid }).strict(),
    z.object({ kind: z.literal('build_feature'), buildId: uuid }).strict(),
    z.object({ kind: z.literal('promote_feature'), buildId: uuid }).strict(),
    // A project and a kind of place, never an address and never an
    // instruction: what to write about is the project's own description.
    z
        .object({
        kind: z.literal('draft_publication'),
        projectId: uuid,
        forHuman: uuid,
        channel: z.enum(['blog', 'forum', 'social', 'newsletter']),
    })
        .strict(),
    // What a person asked to find, as a search phrase and a market. The
    // phrase goes to Places as a query and nowhere else: it names no host and
    // is held to one line.
    z
        .object({
        kind: z.literal('discover_places'),
        query: z
            .string()
            .trim()
            .min(3)
            .max(120)
            .regex(/^[^\u0000-\u001f\u007f]+$/, 'one line of text'),
        marketCode: z.string().regex(/^[A-Z]{2}$/, 'a two-letter country code'),
        limit: z.number().int().min(1).max(20),
        requestedBy: uuid,
    })
        .strict(),
    // The rest name a prospect and nothing else: what it is, where it is and
    // how to reach it are on its row.
    z.object({ kind: z.literal('read_site'), prospectId: uuid }).strict(),
    z.object({ kind: z.literal('enrich_contact'), prospectId: uuid }).strict(),
    // `forHuman`: the person who asked for it again, whose writing guidance it follows (0129).
    z.object({ kind: z.literal('draft_first_touch'), prospectId: uuid, forHuman: uuid.optional() }).strict(),
    // A planned send and nothing else: the message, the campaign and the
    // prospect are on its row, and every check is made again when it runs.
    z.object({ kind: z.literal('send_outreach'), campaignSendId: uuid }).strict(),
    // A lifecycle contact and nothing else: the basis, the address and the
    // suppression are read from the record when the job runs.
    z.object({ kind: z.literal('sync_lifecycle'), lifecycleId: uuid }).strict(),
    z.object({ kind: z.literal('delete_lifecycle'), lifecycleId: uuid }).strict(),
    // The event's name from the closed list; the basis is judged again when it runs.
    z.object({ kind: z.literal('track_lifecycle'), lifecycleId: uuid, event: z.string().regex(/^[a-z_]{1,40}$/) }).strict(),
    // A meeting and nothing else: whose it is, whether capture is still on
    // and which Doc is its notes are read from the row when the job runs.
    z.object({ kind: z.literal('read_meeting_notes'), eventId: uuid }).strict(),
    z.object({ kind: z.literal('reconcile_customerio') }).strict(),
    // A journey's request and nothing else: the mailing, the person and
    // every reason not to send are read from the rows when the job runs.
    z.object({ kind: z.literal('send_mailing'), mailingRequestId: uuid }).strict(),
    z.object({ kind: z.literal('rescan_pending') }).strict(),
    z.object({ kind: z.literal('propose_improvements') }).strict(),
    z.object({ kind: z.literal('sweep_plan'), planId: uuid }).strict(),
    z.object({ kind: z.literal('dry_run_plan'), planId: uuid }).strict(),
    z.object({ kind: z.literal('shred_index') }).strict(),
    z.object({ kind: z.literal('record_research'), requestId: uuid }).strict(),
    z.object({ kind: z.literal('fetch_fx') }).strict(),
    z.object({ kind: z.literal('read_sources') }).strict(),
    z.object({ kind: z.literal('advance_cadences') }).strict(),
    z.object({ kind: z.literal('watch_site'), prospectId: uuid }).strict(),
    // A meeting and which turn: who speaks, the agenda and what was said so
    // far are read from the rows when the job runs, never carried here.
    z.object({ kind: z.literal('meeting_turn'), meetingId: uuid, turn: z.number().int().min(0).max(36) }).strict(),
    z.object({ kind: z.literal('meeting_minutes'), meetingId: uuid }).strict(),
]);
// ---------------------------------------------------------------------------
// Briefs
// ---------------------------------------------------------------------------
// What a brief was built from, so a person can check it rather than trust it.
export const briefSourceSchema = z
    .object({
    kind: z.enum(['message', 'thread', 'calendar_event', 'task', 'contact', 'company', 'flex']),
    id: uuid,
    /** Why this source was used, in one line. Shown beside the brief. */
    note: z.string().max(200).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// The timeline
// ---------------------------------------------------------------------------
export const timelineItemSchema = z
    .object({
    kind: timelineItemKindSchema,
    /** Absent for a break and a note, which are placements with nothing behind them. */
    sourceId: uuid.nullable(),
    at: z.date().nullable(),
    until: z.date().nullable(),
    durationMinutes: z.number().int().positive().nullable(),
    rank: z.number().nullable(),
    pinned: z.boolean(),
    /** A break's or a note's own text. Sealed at rest; opened for its owner. */
    label: z.string().nullable(),
    /** How it got here: computed from the candidate set, or placed by someone. */
    placedBy: z.enum(['candidate', 'human', 'agent']),
    /** An agent that moved something says why, and the reason travels with it. */
    reason: z.string().nullable(),
    /** The agent that placed it, as it acted (a handle or an address); null unless an agent did. */
    actor: z.string().nullable(),
    /** That agent's given name, for "Sol placed it". Null when it is not (or no longer) an agent. */
    actorName: z.string().nullable(),
    /** What started an agent's placement (ADR-0058). Never an inbound message. */
    origin: z.enum(['chat', 'confirmation', 'assignment', 'schedule']).nullable(),
})
    .strict();
/**
 * What a person may ask of a timeline item.
 *
 * Closed, because the frontend renders one control per entry and the backend
 * has to answer every one of them. A seventh action is a backend change
 * first.
 */
export const timelineActionSchema = z.enum([
    'open',
    'reschedule',
    'postpone',
    'hide',
    'pin',
    'undo',
    'brief_me',
    'draft_reply',
    'delegate',
]);
//# sourceMappingURL=work.js.map